import React, { useState, useEffect, Suspense, lazy } from 'react';
import { EzPairingExperience as EzPairingSync } from '@xoxokiosk/ez-pairing';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { kioskInstanceApi, shopApi, recipeApi, orderApi, voiceProfilesApi, getDOTDDrinkColors, generateTravel3Image, uploadBase64Selfie } from '@xoxokiosk/shared';
import { detectPWA } from '../utils/pwaDetection';
import { PWAInstallPrompt } from '../components/PWAInstallPrompt';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button, Input, Label, LoadingSpinner, Alert, AlertDescription } from '@xoxokiosk/ui';
import { AlertTriangle } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: any) {
    console.error('[KIOSK-LIVE] ErrorBoundary caught error:', error);
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('[KIOSK-LIVE] ErrorBoundary componentDidCatch:', error, errorInfo);
  }

  render() {
    if ((this.state as any).hasError) {
      return <div className="xk-ExperienceFull">Something went wrong with EZ Pairing component.</div>;
    }
    return (this.props as any).children;
  }
}

import type { Shop as SharedShop, Recipe as SharedRecipe, OrderRequestMinimal } from '@xoxokiosk/shared/types/api-types';

import type { KioskInstance } from '@xoxokiosk/shared/types/api-types';

// Alias for backward compatibility
type KioskInstanceResponse = KioskInstance;

interface ShopResponse {
  id: string;
  shop_id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

interface AdminRecipesResponse {
  recipes: Recipe[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}
import type { AxiosResponse } from 'axios';
// ordering-terminal styles are imported within the component package (src/index.tsx)

// Define the Shop interface with all required properties
interface Shop {
  shop_id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  recipes?: string[]; // Array of recipe IDs
  settings?: Record<string, unknown>;
  shop?: Shop; // Nested shop property
  order_default_size?: 'sizeS' | 'sizeM' | 'sizeL';
  order_default_quantity?: number;
  order_default_status?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  userId?: string;
}

type Recipe = SharedRecipe;

// Define props for the ToyKiosk component
interface ToyKioskProps {
  instance: KioskInstanceResponse;
  shop: Shop;
  recipes: Recipe[];
  config?: any;
  onOrderSubmit?: (order: any) => void;
  onError?: (error: Error) => void;
}

// Define props for the OrderingTerminal component
interface OrderingTerminalProps {
  instance: KioskInstanceResponse;
  shop: Shop;
  recipes: Recipe[];
  config?: any;
  onOrderSubmit?: (order: any) => Promise<void>;
  onError?: (error: Error) => void;
}

interface StoryTreeProps {
  instance: KioskInstanceResponse;
  shop: Shop;
  recipes: Recipe[];
  config?: any;
  onOrderSubmit?: (order: any) => Promise<void>;
  onError?: (error: Error) => void;
}

interface EzPairingProps {
  instance: KioskInstanceResponse;
  shop: Shop;
  recipes: Recipe[];
  config?: any;
  onOrderSubmit?: (order: any) => Promise<void>;
  onError?: (error: Error) => void;
}

// Define props for the VoiceSommelier component
interface VoiceSommelierProps {
  instance: KioskInstanceResponse;
  shop: Shop;
  recipes: Recipe[];
  config?: any;
  onOrderSubmit?: (order: any) => Promise<void> | void;
  onError?: (error: Error) => void;
}
interface FifteenTwentyProps {
  instance: KioskInstanceResponse;
  shop: Shop;
  recipes: Recipe[];
  config?: any;
  onOrderSubmit?: (order: any) => Promise<void> | void;
  onError?: (error: Error) => void;
}

// Lazy load kiosk components with proper typing
const ToyKiosk = lazy<React.ComponentType<ToyKioskProps>>(
  () => import('@xoxokiosk/toy-kiosk')
    .then(module => ({
      default: module.ToyKiosk as React.ComponentType<ToyKioskProps>
    }))
    .catch(error => {
      console.error('Failed to load ToyKiosk:', error);
      throw error;
    })
);

// Lazy load OrderingTerminal component
const OrderingTerminal = lazy<React.ComponentType<OrderingTerminalProps>>(
  () => import('@xoxokiosk/ordering-terminal')
    .then(module => ({
      default: module.default as React.ComponentType<OrderingTerminalProps>
    }))
    .catch(error => {
      console.error('Failed to load OrderingTerminal:', error);
      throw error;
    })
);

const EzPairing = lazy<React.ComponentType<EzPairingProps>>(
  () => {
    console.log('[KIOSK-LIVE] Attempting to import @xoxokiosk/ez-pairing');
    return import('@xoxokiosk/ez-pairing')
      .then(module => {
        console.log('[KIOSK-LIVE] Import successful, module:', module);
        console.log('[KIOSK-LIVE] EzPairingExperience:', module.EzPairingExperience);
        return {
          default: module.EzPairingExperience as React.ComponentType<EzPairingProps>
        };
      })
      .catch(error => {
        console.error('[KIOSK-LIVE] Failed to load EzPairing:', error);
        throw error;
      });
  }
);

const VibesMatching = lazy<React.ComponentType<EzPairingProps>>(
  () => {
    console.log('[KIOSK-LIVE] Attempting to import @xoxokiosk/vibes-matching');
    return import('@xoxokiosk/vibes-matching')
      .then(module => {
        console.log('[KIOSK-LIVE] Import successful, module:', module);
        return {
          default: module.VibesMatchingExperience as React.ComponentType<EzPairingProps>
        };
      })
      .catch(error => {
        console.error('[KIOSK-LIVE] Failed to load VibesMatching:', error);
        throw error;
      });
  }
);

const StoryTree = lazy<React.ComponentType<StoryTreeProps>>(
  () => import('@xoxokiosk/story-tree')
    .then(module => ({
      default: module.default as React.ComponentType<StoryTreeProps>
    }))
    .catch(error => {
      console.error('Failed to load StoryTree:', error);
      throw error;
    })
);

const MBTIStyleTransfer = lazy<React.ComponentType<StoryTreeProps>>(
  () => import('@xoxokiosk/mbti')
    .then(module => ({
      default: module.default as React.ComponentType<StoryTreeProps>
    }))
    .catch(error => {
      console.error('Failed to load MBTIStyleTransfer:', error);
      throw error;
    })
);

const VoiceSommelier = lazy<React.ComponentType<VoiceSommelierProps>>(
  () => import('@xoxokiosk/voice-sommelier')
    .then((module: any) => ({
      default: (module.VoiceSommelier || module.default) as React.ComponentType<VoiceSommelierProps>
    }))
    .catch(error => {
      console.error('Failed to load VoiceSommelier:', error);
      throw error;
    })
);
const FifteenTwenty = lazy<React.ComponentType<FifteenTwentyProps>>(
  () => import('@xoxokiosk/fifteen-twenty')
    .then((module: any) => ({
      default: (module.FifteenTwenty || module.default) as React.ComponentType<FifteenTwentyProps>
    }))
    .catch(error => {
      console.error('Failed to load FifteenTwenty:', error);
      throw error;
    })
);
// Lazy load FiveMui component
interface FiveMuiProps {
  instance: KioskInstanceResponse;
  shop: Shop;
  recipes: Recipe[];
  config?: any;
  onOrderSubmit?: (order: any) => Promise<void> | void;
  onError?: (error: Error) => void;
}
const FiveMui = lazy<React.ComponentType<FiveMuiProps>>(
  () => import('@xoxokiosk/five-mui')
    .then((module: any) => ({
      default: (module.FiveMui || module.default) as React.ComponentType<FiveMuiProps>
    }))
    .catch(error => {
      console.error('Failed to load FiveMui:', error);
      throw error;
    })
);

// Lazy load DOTD (Drink of the Day) component
interface DOTDProps {
  instance: KioskInstanceResponse;
  shop: Shop;
  recipes: Recipe[];
  drinkColors?: any[];
  config?: any;
  onOrderSubmit?: (order: any) => Promise<any>;
  onGenerateImage?: (params: any) => Promise<{ imageUrl: string }>;
  onError?: (error: Error) => void;
}
const DOTD = lazy<React.ComponentType<DOTDProps>>(
  () => import('@xoxokiosk/dotd')
    .then((module: any) => ({
      default: (module.DOTDExperience || module.default) as React.ComponentType<DOTDProps>
    }))
    .catch(error => {
      console.error('Failed to load DOTD:', error);
      throw error;
    })
);

interface MirakoAvatarProps {
  instance: KioskInstanceResponse;
  shop: Shop;
  recipes: Recipe[];
  config?: any;
  onOrderSubmit?: (order: any) => Promise<any>;
  onError?: (error: Error) => void;
}

const MirakoAvatar = lazy<React.ComponentType<MirakoAvatarProps>>(
  () => import('@xoxokiosk/mirako-avatar')
    .then((module: any) => ({
      default: (module.MirakoAvatarExperience || module.default) as React.ComponentType<MirakoAvatarProps>
    }))
    .catch(error => {
      console.error('Failed to load MirakoAvatarExperience:', error);
      throw error;
    })
);
const KioskLivePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [joinCode, setJoinCode] = useState('');
  const [kioskInstance, setKioskInstance] = useState<KioskInstance | null>(null);
  const [shop, setShop] = useState<Shop | null>(null);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [drinkColors, setDrinkColors] = useState<any[]>([]);
  const [voiceProfile, setVoiceProfile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSafariPrompt, setShowSafariPrompt] = useState(false);
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  const [pwaDismissed, setPwaDismissed] = useState(false);

  /**
   * Transforms raw shop data into a properly typed Shop object
   * @param shopData Raw shop data from API
   * @returns Properly typed Shop object
   * @throws Error if required fields are missing
   */
  const transformShopData = (shopData: unknown): Shop => {
    if (!shopData || typeof shopData !== 'object') {
      throw new Error('Invalid shop data: expected an object');
    }

    const data = shopData as Record<string, unknown>;
    // Validate required fields
    const id = data.shop_id || data.id;
    const name = data.name;
    if (!id || typeof id !== 'string') {
      throw new Error('Invalid shop data: missing or invalid shop_id/id');
    }
    if (!name || typeof name !== 'string') {
      throw new Error('Invalid shop data: missing or invalid name');
    }
    // Transform settings if present
    let settings: Record<string, unknown> = {};
    if (data.settings) {
      try {
        settings = typeof data.settings === 'string' 
          ? JSON.parse(data.settings) 
          : (data.settings as Record<string, unknown>);
      } catch (e) {
        console.warn('Failed to parse shop settings:', e);
      }
    }
    // Transform recipes
    let recipes: string[] = [];
    if (Array.isArray(data.recipes)) {
      recipes = data.recipes
        .map(recipe => 
          typeof recipe === 'string' ? recipe : 
          typeof recipe === 'object' && recipe !== null && 'id' in recipe ? 
            String((recipe as { id: unknown }).id) : 
            null
        )
        .filter((recipe): recipe is string => recipe !== null);
    } else if (data.recipes) {
      recipes = [String(data.recipes)];
    }
    // Normalize order_default_size to union type
    let order_default_size: 'sizeS' | 'sizeM' | 'sizeL' | undefined = undefined;
    if (data.order_default_size) {
      const sizeStr = String(data.order_default_size);
      if (sizeStr === 'sizeS' || sizeStr === 'sizeM' || sizeStr === 'sizeL') {
        order_default_size = sizeStr;
      }
    }
    // Normalize userId
    let userId: string | undefined = undefined;
    if (typeof data.user_id === 'string') {
      userId = data.user_id;
    } else if (typeof data.userId === 'string') {
      userId = data.userId;
    }
    // Build the shop object with proper typing
    const shop: Shop = {
      shop_id: id,
      name,
      created_at: typeof data.created_at === 'string' ? data.created_at : new Date().toISOString(),
      updated_at: typeof data.updated_at === 'string' ? data.updated_at : new Date().toISOString(),
      recipes,
      settings,
      ...(data.order_default_quantity !== undefined && { order_default_quantity: Number(data.order_default_quantity) }),
      ...(order_default_size && { order_default_size }),
      ...(data.order_default_status !== undefined && { order_default_status: String(data.order_default_status) as Shop['order_default_status'] }),
      ...(data.active_instances_count !== undefined && { active_instances_count: Number(data.active_instances_count) }),
      ...(data.admin_id !== undefined && { admin_id: String(data.admin_id) }),
      ...(userId && { userId }),
    };
    return shop;
  };

  // Check for join code in URL on initial load
  useEffect(() => {
    const codeFromUrl = searchParams.get('joinCode');
    if (codeFromUrl) {
      setJoinCode(codeFromUrl);
      void loadKioskData(codeFromUrl);
    }
  }, [searchParams]);

  // Detect iOS devices and prompt for Safari usage
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInWebView = !/Safari/.test(navigator.userAgent) && /Mobile/.test(navigator.userAgent);
    const isStandalone = window.navigator.standalone === true;
    const isSmallViewport = window.innerWidth < 768 || window.innerHeight < 600;
    const promptDismissed = sessionStorage.getItem('safariPromptDismissed') === 'true';

    // Show Safari prompt if:
    // 1. It's an iOS device
    // 2. Not in Safari (likely in a web view or small window)
    // 3. Not in standalone mode (PWA)
    // 4. Has small viewport (indicating it's in a preview window)
    // 5. Prompt hasn't been dismissed this session
    if (isIOS && (isInWebView || isSmallViewport) && !isStandalone && !promptDismissed) {
      setShowSafariPrompt(true);
    }
  }, []);

  // Detect PWA mode for persistent storage
  useEffect(() => {
    const pwaStatus = detectPWA();
    const dismissed = sessionStorage.getItem('pwaPromptDismissed') === 'true';
    
    if (!pwaStatus.isPWA && !dismissed) {
      setShowPWAPrompt(true);
    }
  }, []);

  const dismissPWAPrompt = () => {
    setShowPWAPrompt(false);
    setPwaDismissed(true);
    sessionStorage.setItem('pwaPromptDismissed', 'true');
  };

  const dismissSafariPrompt = () => {
    setShowSafariPrompt(false);
    // Store in sessionStorage to not show again during this session
    sessionStorage.setItem('safariPromptDismissed', 'true');
  };

  /**
   * Loads kiosk data by join code
   * @param code The join code for the kiosk
   */
  const loadKioskData = async (code: string) => {
    if (!code || typeof code !== 'string') {
      setError('Invalid join code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Fetch kiosk instance
      console.log('Fetching kiosk instance with code:', code);
      const response = await kioskInstanceApi.getKioskInstanceByCode(code);

      if (!response?.data) {
        throw new Error('No response data received from server');
      }

      // Assign to a loose-typed variable first
      const rawInstance = response.data as any;
      // Normalize shop.recipes if present
      if (rawInstance.shop) {
        if (typeof rawInstance.shop.recipes === 'string') {
          rawInstance.shop.recipes = [rawInstance.shop.recipes];
        } else if (!Array.isArray(rawInstance.shop.recipes)) {
          rawInstance.shop.recipes = [];
        }
      }
      // If shop is missing but shop_id exists, fetch shop and normalize
      if (!rawInstance.shop && rawInstance.shop_id) {
        console.log('Fetching shop data for ID:', rawInstance.shop_id);
        try {
          const shopResponse = await shopApi.getShopById(rawInstance.shop_id);
          if (shopResponse?.data) {
            let normalizedShop = { ...shopResponse.data };
            if (typeof normalizedShop.recipes === 'string') {
              normalizedShop.recipes = [normalizedShop.recipes];
            } else if (!Array.isArray(normalizedShop.recipes)) {
              normalizedShop.recipes = [];
            }
            rawInstance.shop = normalizedShop;
          }
        } catch (shopError) {
          console.error('Error fetching shop data:', shopError);
          throw new Error(`Failed to fetch shop: ${shopError instanceof Error ? shopError.message : 'Unknown error'}`);
        }
      }

      // Now cast to the strict type
      const instance: KioskInstanceResponse = rawInstance;
      console.log('Kiosk instance loaded:', instance);

      if (!instance.shop) {
        throw new Error('Kiosk is not associated with a valid shop');
      }

      // 3. Transform shop data
      const shop = transformShopData(instance.shop);

      // 4. Fetch recipes
      let recipes: Recipe[] = [];
      try {
        const recipesResponse = await recipeApi.getAdminRecipes(shop.shop_id);
        if (recipesResponse?.data?.recipes) {
          recipes = Array.isArray(recipesResponse.data.recipes)
            ? recipesResponse.data.recipes as Recipe[]
            : [];
        }
      } catch (recipeError) {
        console.warn('Could not fetch recipes, using empty array:', recipeError);
      }

      // 5. Fetch voice profile for five-mui kiosk type with fallback: instance -> shop default -> global default
      let voiceProfileData = null;
      if (instance.kiosk_type_identifier === 'five-mui') {
        try {
          // Parse instance config
          let config: any = {};
          try {
            config = typeof instance.config === 'string'
              ? JSON.parse(instance.config)
              : (typeof instance.config === 'object' && instance.config !== null ? instance.config : {});
          } catch (e) {
            console.warn('[VoiceProfile] Failed to parse instance config:', instance.config);
            config = {};
          }

          // Resolve fallback chain
          const instanceVoiceProfileId = typeof config?.voiceProfileId === 'string' ? config.voiceProfileId : undefined;
          const shopSettings = (shop?.settings || {}) as Record<string, unknown>;
          const shopDefaultVoiceProfileId = typeof shopSettings?.voiceProfileId === 'string' ? (shopSettings.voiceProfileId as string) : undefined;
          const envDefaultVoiceProfileId = (import.meta as any).env?.VITE_DEFAULT_VOICE_PROFILE_ID as string | undefined;
          const resolvedVoiceProfileId = instanceVoiceProfileId || shopDefaultVoiceProfileId || envDefaultVoiceProfileId;

          if (resolvedVoiceProfileId) {
            const voiceProfileResponse = await voiceProfilesApi.getProfile(resolvedVoiceProfileId);
            voiceProfileData = voiceProfileResponse.data;
          } else {
            console.warn('[VoiceProfile] No voice profile ID resolved (instance/shop/env). Audio cues will be disabled.');
          }
        } catch (voiceError) {
          console.warn('[VoiceProfile] Could not fetch voice profile:', voiceError);
        }
      }

      // 6. Update state with loaded data
      setKioskInstance(instance);
      setShop(shop);
      setRecipes(recipes);
      setVoiceProfile(voiceProfileData);

      // 7. Load drink colors for DOTD kiosk type
      if (instance.kiosk_type_identifier === 'dotd') {
        try {
          console.log('[DOTD] Loading drink colors for instance:', code);
          const colorsResponse = await getDOTDDrinkColors(code);
          if (colorsResponse.drinks && colorsResponse.drinks.length > 0) {
            console.log('[DOTD] Loaded', colorsResponse.drinks.length, 'drink colors');
            setDrinkColors(colorsResponse.drinks);
          } else {
            console.log('[DOTD] No drink colors found, recommendations will be unavailable');
          }
        } catch (colorsError) {
          console.warn('[DOTD] Could not load drink colors:', colorsError);
        }
      }

    } catch (err) {
      console.error('Error in loadKioskData:', err);
      const errorMessage = err instanceof Error 
        ? `Failed to load kiosk: ${err.message}`
        : 'An unknown error occurred while loading the kiosk';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles form submission for joining a kiosk
   * @param e Form event
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedCode = joinCode.trim();
    if (!trimmedCode) {
      setError('Please enter a join code');
      return;
    }
    
    try {
      // Update URL with the join code
      navigate(`/kiosk/live?joinCode=${encodeURIComponent(trimmedCode)}`, { 
        replace: true,
        state: { from: 'kiosk-join' }
      });
      
      // Load kiosk data
      void loadKioskData(trimmedCode);
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setError(
        error instanceof Error 
          ? `Failed to join kiosk: ${error.message}`
          : 'An unknown error occurred while joining the kiosk'
      );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-linear-to-br from-background via-background/95 to-background">
        <LoadingSpinner size="lg" text="Loading kiosk data..." />
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-linear-to-br from-background via-background/95 to-background">
        <Card className="max-w-md w-full border-destructive/50 bg-card/80 backdrop-blur-sm">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="flex justify-center">
              <div className="rounded-full bg-destructive/10 p-3">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold">Live Code Mismatch</h2>
              <p className="text-sm text-muted-foreground">
                Live code mismatch with active instance. Please check your code and try again.
              </p>
            </div>
            <Button
              onClick={() => navigate('/kiosk/live')}
              className="w-full bg-linear-to-r from-pink-500 to-cyan-500 hover:from-pink-600 hover:to-cyan-600"
            >
              Back to Join
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (kioskInstance && shop && recipes) {
    // Use kiosk_type_identifier from the top-level instance property
    const kioskTypeIdentifier = kioskInstance?.kiosk_type_identifier;
    const kioskType = kioskInstance?.kiosk_type || kioskInstance?.kioskType?.config_identifier;
    
    console.log('[KIOSK-LIVE] Experience selection debug:', {
      kioskTypeIdentifier,
      kioskType,
      kioskInstance,
      shop: shop?.shop_id
    });

    // Shared order submit handler for both ToyKiosk and OrderingTerminal
    const handleOrderSubmit = async (order: OrderRequestMinimal) => {
      const recipe = recipes.find(r => r.id === order.recipeId);
      if (!recipe) throw new Error('Recipe not found');
      const glassName = recipe.glass?.name || '';
      const size = shop.order_default_size || 'sizeS';
      const quantity = shop.order_default_quantity || 1;
      const status = shop.order_default_status || 'PENDING';
      const volume = (recipe.glass && typeof recipe.glass === 'object' && size in recipe.glass) ? (recipe.glass as any)[size] : 0;
      const userId = shop.userId || '';
      const targetShopId = shop.shop_id;
      const name = order.name || '';
      const minimalPayload = {
        recipeId: order.recipeId,
        targetShopId,
        kioskInstanceCode: kioskInstance.join_code,
        name,
      };
      const response = await orderApi.createOrder(minimalPayload);
      // Return data upward so the experience can read displayId
      return response?.data ?? response;
    };

    let instanceConfig: any = {};
    try {
      instanceConfig = typeof kioskInstance.config === 'string'
        ? JSON.parse(kioskInstance.config)
        : (typeof kioskInstance.config === 'object' && kioskInstance.config !== null ? kioskInstance.config : {});
    } catch (e) {
      console.warn('[KIOSK-LIVE] Failed to parse instance config:', kioskInstance.config);
      instanceConfig = {};
    }

    console.log('[KIOSK-LIVE] Switching on kioskTypeIdentifier:', kioskTypeIdentifier);
    
    switch (kioskTypeIdentifier) {
      case 'mirako-avatar':
        return (
          <div className="xk-ExperienceFull">
            <Suspense fallback={<div className="xk-ExperienceFull" /> }>
              <MirakoAvatar
                instance={kioskInstance}
                shop={shop}
                recipes={recipes}
                config={instanceConfig}
                onOrderSubmit={handleOrderSubmit}
              />
            </Suspense>
          </div>
        );
      case 'toy-kiosk':
        console.log('[KIOSK-LIVE] Loading toy-kiosk experience');
        return (
          <div className="xk-ExperienceFull">
            <Suspense fallback={<div className="xk-ExperienceFull" /> }>
              <ToyKiosk 
                instance={kioskInstance} 
                shop={shop} 
                recipes={recipes}
                onOrderSubmit={handleOrderSubmit}
              />
            </Suspense>
          </div>
        );
      case 'ez-pairing':
        console.log('[KIOSK-LIVE] Loading ez-pairing experience with props:', {
          instance: kioskInstance,
          shop,
          recipesCount: recipes.length
        });
        const useSyncEz = (import.meta as any).env?.VITE_EZ_PAIRING_SYNC === 'true';
        if (useSyncEz) {
          console.warn('[KIOSK-LIVE] VITE_EZ_PAIRING_SYNC is true — rendering EzPairing synchronously (debug mode)');
          return (
            <div className="xk-ExperienceFull">
              <ErrorBoundary>
                <EzPairingSync 
                  instance={kioskInstance} 
                  shop={shop} 
                  recipes={recipes}
                  onOrderSubmit={handleOrderSubmit}
                />
              </ErrorBoundary>
            </div>
          );
        }
        return (
          <div className="xk-ExperienceFull">
            <ErrorBoundary>
              <Suspense fallback={
                <div className="xk-ExperienceFull">
                  <div>Loading EZ Pairing...</div>
                  {console.log('[KIOSK-LIVE] EZ Pairing Suspense fallback triggered')}
                </div>
              }>
                <EzPairing 
                  instance={kioskInstance} 
                  shop={shop} 
                  recipes={recipes}
                  onOrderSubmit={handleOrderSubmit}
                />
              </Suspense>
            </ErrorBoundary>
          </div>
        );
      case 'vibes-matching':
        console.log('[KIOSK-LIVE] Loading vibes-matching experience with props:', {
          instance: kioskInstance,
          shop,
          recipesCount: recipes.length
        });
        return (
          <div className="xk-ExperienceFull">
            <ErrorBoundary>
              <Suspense fallback={
                <div className="xk-ExperienceFull">
                  <div>Loading Vibes Matching...</div>
                </div>
              }>
                <VibesMatching 
                  instance={kioskInstance} 
                  shop={shop} 
                  recipes={recipes}
                  onOrderSubmit={handleOrderSubmit}
                />
              </Suspense>
            </ErrorBoundary>
          </div>
        );
      case 'ordering-terminal':
        return (
          <div className="xk-ExperienceFull">
            <Suspense fallback={<div className="xk-ExperienceFull" /> }>
              <OrderingTerminal 
                instance={kioskInstance} 
                shop={shop} 
                recipes={recipes}
                onOrderSubmit={handleOrderSubmit}
                onError={(error) => {
                  console.error('Order error:', error);
                  // Handle error (e.g., show error message to user)
                }}
              />
            </Suspense>
          </div>
        );
      case 'story-tree':
        return (
          <div className="xk-ExperienceFull">
            <Suspense fallback={<div className="xk-ExperienceFull" /> }>
              <StoryTree 
                instance={kioskInstance} 
                shop={shop} 
                recipes={recipes}
                onOrderSubmit={handleOrderSubmit}
                onError={(error) => {
                  console.error('Order error:', error);
                  // Handle error (e.g., show error message to user)
                }}
              />
            </Suspense>
          </div>
        );
      case 'mbti':
        return (
          <div className="xk-ExperienceFull">
            <Suspense fallback={<div className="xk-ExperienceFull" /> }>
              <MBTIStyleTransfer 
                instance={kioskInstance} 
                shop={shop} 
                recipes={recipes}
                onOrderSubmit={handleOrderSubmit}
                onError={(error) => {
                  console.error('MBTI Style Transfer error:', error);
                  // Handle error (e.g., show error message to user)
                }}
              />
            </Suspense>
          </div>
        );
      case 'voice-sommelier':
        return (
          <div className="xk-ExperienceFull">
            <Suspense fallback={<div className="xk-ExperienceFull" /> }>
              <VoiceSommelier
                instance={kioskInstance}
                shop={shop}
                recipes={recipes}
                onOrderSubmit={handleOrderSubmit}
                onError={(error) => {
                  console.error('VoiceSommelier error:', error);
                }}
              />
            </Suspense>
          </div>
        );
      case 'fifteen-twenty':
        return (
          <div className="xk-ExperienceFull">
            <Suspense fallback={<div className="xk-ExperienceFull" /> }>
              <FifteenTwenty
                instance={kioskInstance}
                shop={shop}
                recipes={recipes}
                onOrderSubmit={handleOrderSubmit}
                onError={(error) => {
                  console.error('FifteenTwenty error:', error);
                }}
              />
            </Suspense>
          </div>
        );
      case 'five-mui':
        return (
          <div className="xk-ExperienceFull">
            <Suspense fallback={<div className="xk-ExperienceFull" /> }>
              <FiveMui
                instance={kioskInstance}
                shop={shop}
                recipes={recipes}
                config={{
                  audioProfile: voiceProfile?.cues || {}
                }}
                onOrderSubmit={handleOrderSubmit}
                onError={(error) => {
                  console.error('FiveMui error:', error);
                }}
              />
            </Suspense>
          </div>
        );
      case 'dotd':
        console.log('[KIOSK-LIVE] Loading DOTD (Drink of the Day) experience');
        return (
          <div className="xk-ExperienceFull">
            <Suspense fallback={
              <div className="xk-ExperienceFull">
                <div>Loading Drink of the Day...</div>
              </div>
            }>
              <DOTD
                instance={kioskInstance}
                shop={shop}
                recipes={recipes}
                drinkColors={drinkColors}
                onOrderSubmit={handleOrderSubmit}
                onGenerateImage={async (params) => {
                  console.log('[DOTD] Generating image with params:', params);
                  try {
                    // 1. Upload selfie to S3 if it's a base64 image
                    let selfieUrl = params.selfieUrl;
                    if (selfieUrl.startsWith('data:')) {
                      console.log('[DOTD] Uploading selfie to S3...');
                      const uploadResult = await uploadBase64Selfie(selfieUrl, `dotd-${Date.now()}`);
                      selfieUrl = uploadResult.url;
                      console.log('[DOTD] Selfie uploaded:', selfieUrl);
                    }
                    
                    // 2. Build material URLs - selfie + drink image if available
                    const materialUrls = [selfieUrl];
                    if (params.drinkImageUrl) {
                      materialUrls.push(params.drinkImageUrl);
                    }
                    
                    // 3. Build a detailed prompt referencing face, clothes, drink, pose, and gender
                    const outfitColorsText = params.outfitColors?.length > 1 
                      ? `featuring colors: ${params.outfitColors.slice(0, 4).join(', ')}`
                      : `in ${params.outfitColor} color`;
                    
                    const genderText = params.gender 
                      ? `The subject is ${params.gender}. Style the outfit appropriately for ${params.gender === 'male' ? 'men' : 'women'}.`
                      : `Infer the subject's gender from the photo and style the outfit appropriately.`;
                    
                    const detailedPrompt = params.prompt || [
                      `Create a stylish fashion portrait photograph.`,
                      `Reference the person's face and facial features from the first image.`,
                      genderText,
                      `Expand on the outfit that the person is wearing ${outfitColorsText}. You can add complementary fashion elements, but keep the core outfit style the same.`,
                      params.drinkImageUrl ? `They are elegantly holding this drink (${params.drinkColor} colored) as shown in the second reference image.` : `They are elegantly holding a ${params.drinkName} drink with ${params.drinkColor} color.`,
                      `The pose should be ${params.poseDescription} - ${params.pose === 'expressive' ? 'dynamic and energetic with bold gestures' : params.pose === 'confident' ? 'powerful and self-assured stance' : 'soft and approachable with gentle movements'}.`,
                      `High fashion photography style, professional lighting, vibrant colors, magazine quality.`
                    ].join(' ');
                    
                    // 4. Call Travel3 API to generate styled image
                    console.log('[DOTD] Calling Travel3 API with', materialUrls.length, 'images...');
                    const result = await generateTravel3Image({
                      materialUrls,
                      prompt: detailedPrompt,
                    });
                    
                    console.log('[DOTD] Travel3 result:', result);
                    return { imageUrl: result.resultUrl || result.url || selfieUrl };
                  } catch (err) {
                    console.error('[DOTD] Image generation failed:', err);
                    throw err;
                  }
                }}
                onError={(error) => {
                  console.error('DOTD error:', error);
                }}
              />
            </Suspense>
          </div>
        );
      default:
        return (
          <div className="flex items-center justify-center min-h-screen p-4 bg-linear-to-br from-background via-background/95 to-background">
            <Card className="max-w-md w-full border-destructive/50 bg-card/80 backdrop-blur-sm">
              <CardContent className="pt-6 text-center space-y-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-destructive/10 p-3">
                    <AlertTriangle className="h-8 w-8 text-destructive" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold">Unsupported Kiosk Type</h2>
                  <p className="text-sm text-muted-foreground">
                    The kiosk type "{kioskType || kioskTypeIdentifier}" is not supported.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/')}
                  className="w-full bg-linear-to-r from-pink-500 to-cyan-500 hover:from-pink-600 hover:to-cyan-600"
                >
                  Return to Home
                </Button>
              </CardContent>
            </Card>
          </div>
        );
    }
  }

  return (
    <>
      {/* Safari Prompt for iOS devices */}
      {showSafariPrompt && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="max-w-sm w-full border-primary/50 bg-card/95 backdrop-blur-md shadow-2xl">
            <CardContent className="pt-6 text-center space-y-4">
              <div className="flex justify-center">
                <div className="rounded-full bg-primary/10 p-3">
                  <AlertTriangle className="h-12 w-12 text-primary" />
                </div>
              </div>
              
              <div className="space-y-2">
                <h2 className="text-xl font-bold">Open in Safari</h2>
                <p className="text-sm text-muted-foreground">
                  For the best experience, please open this page in Safari browser.
                </p>
              </div>
              
              <div className="space-y-3 pt-2">
                <Button
                  onClick={openInSafari}
                  className="w-full bg-primary hover:bg-primary/90"
                >
                  Open in Safari
                </Button>
                <Button
                  onClick={dismissSafariPrompt}
                  variant="outline"
                  className="w-full"
                >
                  Continue Anyway
                </Button>
                <div className="text-xs text-muted-foreground pt-2 space-y-1">
                  <p className="font-medium">Or manually:</p>
                  <p>1. Tap the share button ⬆️</p>
                  <p>2. Select "Open in Safari"</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* PWA Install Prompt */}
      {showPWAPrompt && (
        <PWAInstallPrompt 
          onDismiss={dismissPWAPrompt}
          onInstall={() => {
            dismissPWAPrompt();
            window.location.reload();
          }}
        />
      )}

      <div className="flex items-center justify-center min-h-screen p-4 bg-linear-to-br from-background via-background/95 to-background relative overflow-hidden">
        {/* Animated background gradients */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-[10%] left-[5%] w-[900px] h-[600px] bg-pink-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-[15%] right-[5%] w-[700px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <Card className="max-w-md w-full mx-auto border-border/50 bg-card/80 backdrop-blur-md shadow-2xl relative z-10">
          <CardHeader className="text-center space-y-4 pb-4">
            {/* Logo */}
            <div className="flex justify-center">
              <img
                src="/company-logo.svg"
                alt="Company Logo"
                className="max-h-11 object-contain drop-shadow-lg"
                loading="eager"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            
            {/* Icon */}
            <div className="flex justify-center">
              <div className="w-18 h-18 rounded-full bg-linear-to-br from-pink-500/30 to-cyan-500/30 flex items-center justify-center shadow-inner">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
                  <path d="M3 5h18l-8.586 8.586A2 2 0 0 1 10.172 14H10v5l4 2v1H10H8v-1l4-2v-5h-.172a2 2 0 0 1-1.414-.586L3 5Z" stroke="#ff6ea8" strokeWidth="1.5"/>
                  <path d="M6 5l6 6 6-6" stroke="#27d0c6" strokeWidth="1.5"/>
                  <path d="M7 3l3 2" stroke="#ffb57f" strokeWidth="1.5"/>
                </svg>
              </div>
            </div>

            <div className="space-y-2">
              <CardTitle className="text-3xl font-black tracking-tight">Enter Kiosk Code</CardTitle>
              <CardDescription className="text-base">Join the fun — your cocktail adventure awaits</CardDescription>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="joinCode" className="text-sm font-medium">Join Code</Label>
                <Input
                  id="joinCode"
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="e.g., ABC123"
                  className="text-xl h-14 font-mono tracking-wider text-center bg-background/50 border-border/50 focus-visible:ring-pink-500 focus-visible:border-pink-500"
                  required
                  autoFocus
                  maxLength={6}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  The code is case-insensitive and 6 characters long
                </p>
              </div>
              
              <Button
                type="submit"
                disabled={!joinCode.trim()}
                className="w-full h-14 text-lg font-bold bg-linear-to-r from-pink-500 to-cyan-500 hover:from-pink-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 relative overflow-hidden group"
              >
                <span className="relative z-10">Join Kiosk</span>
                {/* Shimmer effect */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-linear-to-r from-transparent via-white/20 to-transparent" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default KioskLivePage;
